import React, {useState, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import SMCLabel from '../../../components/Simple/SMCLabel';
import SMCModalButtonContainer from '../../../components/core/SMCModal/SMCModalButtonContainer';
import SMCModalBody from '../../../components/core/SMCModal/SMCModalBody';
import SMCButton from '../../../components/Simple/SMCButton';
import Api from '../../../api/SMPFetchHelper';
import SMCTable from '../../../components/core/SMCTable/SMCTable';
import {ObjectTypes} from '../../../api/consts';
import SMCIcon from '../../../components/Simple/SMCIcon';
import SMCTextInput from '../../../components/Simple/SMCTextInput';
import {ServiceDomainGetListSchema} from '../../../api/DBSchema/ServiceDomain';
import SMCPageLoading from '../../../components/core/pageComponents/SMCPageLoading';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Sepperator from '../../../components/Simple/Sepperator';

const enabledCell = React.memo(({value}) => {
    if (value === true) {
        return <SMCIcon icon="greenCheck" alt="enabled" />
    } else if (value === false) {
        return <SMCIcon icon="redX" alt="disabled" />
    } else {
        return null;
    }
});

function FirmwareWizardCustomerHotfix({
    onCancel,
    onFinish,
    setWizardStep,
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    
    const [firmwareId, setFirmwareId] = useState('');
    const [serviceDomainList, setServiceDomainList] = useState([]);
    const [selectedServiceDomains, setSelectedServiceDomains] = useState([]);

    const [searchString, setSearchString] = useState('');
    const [serviceDomainFilteredList, setServiceDomainFilteredList] = useState([]);

    const [isLoaded, setIsLoaded] = useState(false);
    const [isAddingFirmware, setIsAddingFirmware] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isDone, setIsDone] = useState(false);

    const firmwareTableColumns = useMemo(() => [
        { id: 'name',        header: t('com.sofaware.util.TYPE_PORTAL'), accessor: ServiceDomainGetListSchema.name,        width: 400                                    },
        { id: 'description', header: t('SMP.DESCRIPTION'),               accessor: ServiceDomainGetListSchema.description, width: 177                                    },
        { id: 'enabled',     header: t('SMP.ENABLED'),                   accessor: ServiceDomainGetListSchema.enabled,     width: 66, align: 'center', Cell: enabledCell }
    ], [t]);

    const doneButtonFunction = () => {
        if (isDone) {
            onFinish();
            return;
        }
        if (!firmwareId) {
            alert('Must enter firmware ID');
            return;
        }
        if (!selectedServiceDomains.length) {
            alert('Must select at least one service domain');
            return;
        }
        setIsAddingFirmware(true);
        Api.createPrivateFirmware(firmwareId, selectedServiceDomains)
            .then(() => {
                setIsDone(true);
            })
            .catch(e => {
                alert(getErrorMessage(e));
            })
            .finally(() => {
                setIsAddingFirmware(false);
            })
    }

    const backButtonFunction = () => {
        setWizardStep('CHOOSE');
    }

    useEffect(() => {
        Api.fetchObjectList(ObjectTypes.SERVICE_DOMAIN)
            .then(resp => {
                setServiceDomainList(resp.data.data);
                setIsLoaded(true);
            })
            .catch(e => {
                setHasError(true);
                alert(getErrorMessage(e));
            });
    }, [getErrorMessage]);

    useEffect(() => {
        if (serviceDomainList && searchString) {
            const filteredList = serviceDomainList.filter(serviceDomain => (
                serviceDomain &&
                serviceDomain[ServiceDomainGetListSchema.name] &&
                serviceDomain[ServiceDomainGetListSchema.name].toLowerCase().includes(searchString.toLowerCase())
            ));
            setServiceDomainFilteredList(filteredList);
        }
        else {
            setServiceDomainFilteredList(serviceDomainList);
        }
    }, [serviceDomainList, searchString]);

    return (
        <>
        <SMCModalBody lineHeight="normal">
            {
                (() => {
                    if (hasError) {
                        return <SMCLabel>Error adding firmware, please try again</SMCLabel>;
                    }
                    if (!isLoaded || isAddingFirmware) {
                        return <SMCPageLoading />;
                    }
                    if (isDone) {
                        return <SMCLabel>Succesfuly added firmware</SMCLabel>;
                    }
                    return (
                        <>
                        <SMCLabel>Enter Customer Hotfix Firmware ID:</SMCLabel>
                        <SMCTextInput
                            value={firmwareId}
                            onChange={e => setFirmwareId(e.target.value)}
                            label={t('SMC_FIRMWARE_TYPE_PRIVATE_FIRMWARE_ID')}
                            tooltip={t('SMC_FIRMWARE_TYPE_PRIVATE_FIRMWARE_ID_TOOLTIP')}
                        />
                        {/* <EmptyRow /> */}
                        <Sepperator />
                        <SMCTextInput
                            allowClearAll
                            suffixIcon="search"
                            value={searchString}
                            onChange={e => setSearchString(e.target.value)}
                            placeholder={t('SMC_DOMAIN_WIZ_CHOOSE_SEARCH')}
                        />                        
                        {/* Service domains */}
                        <SMCTable
                            columns={firmwareTableColumns}
                            content={serviceDomainFilteredList}
                            rowIdSelector={obj => obj[ServiceDomainGetListSchema.name]}
                            selectedObjects={selectedServiceDomains}
                            setSelectedObjects={setSelectedServiceDomains}
                            hasCheckAll={true}
                            height={400}
                            width={800}
                        />
                        </>
                    )
                })()
            }
        </SMCModalBody>
        <SMCModalButtonContainer>
            <SMCButton onClick={backButtonFunction} style={{ marginRight: 5 }}>
                <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="arrow-circle-left" />
                {t('SMC_GEN_WIZBACK')}
            </SMCButton>
            <SMCButton onClick={doneButtonFunction} disabled={!isLoaded || isAddingFirmware} style={{ marginRight: 5 }}>
                <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                {t('SMC_GEN_DONE')}
            </SMCButton>
            <SMCButton onClick={onCancel}>
                <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                {t('SMC_GEN_CANCEL')}
            </SMCButton>
        </SMCModalButtonContainer>
        </>
    );
}

export default FirmwareWizardCustomerHotfix;